import axios, { AxiosInstance, AxiosProgressEvent, AxiosResponse } from 'axios';
import axiosRetry from 'axios-retry';
import query_string_utils from '../libs/queryStringUtils';
import { errorInterceptor } from './ErrorInterceptor';
import { sessionIdInterceptor } from './SessionID';
import {
  AddIntakePhotosInfo,
  CustomerId,
  DocsResponse,
  MissionCustomer,
  Pilot,
  PilotApiValidationResults,
  ScanDate,
  SiteId,
  UpdateIntakePhotosInfo,
  Upload,
  ValidationResult,
} from '../types';
import withJWTRefreshment from './TokenService/withJWTRefreshment';
import { AssociatedMissionPayload, InvoicesList, ScanCostType, ServerConfig, TimeMaterialsEntryPayload } from './types';
import { PERMISSIONS_TO_VIEW_MISSIONS } from '../consts/permissions';
import { injectTokenToConfig } from './TokenService/injectTokenToConfig';
import { UploadType } from '../consts/uploadType';
import { retryCondition } from './retryCondition';

export enum FileUploadContext {
  PilotDocuments = 'pilot-documents',
  FlightMarkersCoordinates = 'flight-markers-coordinates',
  FlightMarkersImages = 'flight-markers-images',
}

// Overrides the service-wide `retries: Infinity` (see constructor) for invoice submit/edit calls
// specifically: a pilot or office user waiting on a stuck submit button shouldn't retry a persistent
// 500 forever, so these get a small, bounded number of attempts with a quick exponential backoff
// before the error surfaces to the caller. `retryCondition` isn't set here, so it still inherits the
// instance's condition (401/502/504 always, plus 5xx on idempotent requests).
const INVOICE_RETRY_CONFIG = {
  'axios-retry': {
    retries: 3,
    retryDelay: axiosRetry.exponentialDelay,
  },
};

class PilotApiService {
  pilotApiService: AxiosInstance;

  constructor(pilotApiServiceBaseUrl: string) {
    this.pilotApiService = axios.create({
      baseURL: pilotApiServiceBaseUrl,
    });
    this.pilotApiService = withJWTRefreshment(this.pilotApiService);

    axiosRetry(this.pilotApiService, {
      retries: Infinity,
      retryDelay: () => 1000,
      retryCondition,
    });

    this.pilotApiService.interceptors.request.use((config) => sessionIdInterceptor(config));
    this.pilotApiService.interceptors.response.use(
      (res) => res,
      (err) => errorInterceptor(err),
    );
  }

  async getMissions(requestFlightSchedules: boolean) {
    const config = await injectTokenToConfig({}, null, PERMISSIONS_TO_VIEW_MISSIONS);

    const data = await this.pilotApiService
      .get('missions', {
        ...config,
        params: {
          requestFlightSchedules,
        },
      })
      .then((response: AxiosResponse<{ customers: MissionCustomer[] }>) => {
        return response.data.customers;
      });

    cleanUpMissionData(data);

    return data;
  }

  async getPilotInfo() {
    const config = await injectTokenToConfig({}, null, PERMISSIONS_TO_VIEW_MISSIONS);

    return this.pilotApiService.get('pilot', config).then((response: AxiosResponse<Pilot | null>) => {
      return response?.data ?? null;
    });
  }

  async getIntakeFlow(
    customerId: CustomerId,
    siteId: SiteId,
    uploadType: UploadType,
    date: ScanDate,
    flightScheduleId?: string,
  ) {
    const config = await injectTokenToConfig(
      {},
      {
        customer: customerId,
        site: siteId,
      },
    );

    const entityUrlPart = uploadTypeToPilotApiUrlPart(uploadType);
    const queryParams = flightScheduleId ? `?flightScheduleId=${flightScheduleId}` : '';

    return this.pilotApiService
      .get(`customers/${customerId}/sites/${siteId}/${entityUrlPart}/${date}${queryParams}`, config)
      .then((response: AxiosResponse<{ upload: Upload }>) => {
        return response.data.upload;
      });
  }

  async startIntakeFlow(
    signal: AbortSignal,
    customerId: CustomerId,
    siteId: SiteId,
    uploadType: UploadType,
    date: ScanDate,
    flightScheduleId?: string,
    pilotConfirmedDate?: ScanDate,
    expectedPhotoDate?: ScanDate,
  ) {
    const config = await injectTokenToConfig(
      {
        signal,
      },
      {
        customer: customerId,
        site: siteId,
      },
    );

    const entityUrlPart = uploadTypeToPilotApiUrlPart(uploadType);

    // Both dates only exist alongside a schedule; self-service uploads pick their own date.
    const payload = flightScheduleId
      ? {
          flightScheduleId,
          ...(pilotConfirmedDate ? { pilotConfirmedDate } : {}),
          ...(expectedPhotoDate ? { expectedPhotoDate } : {}),
        }
      : {};

    return this.pilotApiService.post(
      `customers/${customerId}/sites/${siteId}/${entityUrlPart}/${date}`,
      payload,
      config,
    );
  }

  async updateIntakeFlow(
    signal: AbortSignal,
    customerId: CustomerId,
    siteId: SiteId,
    uploadType: UploadType,
    date: ScanDate,
    updateBody: {
      photos: UpdateIntakePhotosInfo | AddIntakePhotosInfo;
    },
    flightScheduleId?: string,
  ) {
    const config = await injectTokenToConfig(
      {
        signal,
      },
      {
        customer: customerId,
        site: siteId,
      },
    );
    const entityUrlPart = uploadTypeToPilotApiUrlPart(uploadType);
    const payload = flightScheduleId ? { flightScheduleId } : {};

    return this.pilotApiService.put(
      `customers/${customerId}/sites/${siteId}/${entityUrlPart}/${date}`,
      { ...updateBody, ...payload },
      config,
    );
  }

  async markIntakeLoaded(
    signal: AbortSignal,
    customerId: CustomerId,
    siteId: SiteId,
    uploadType: UploadType,
    date: ScanDate,
    flightScheduleId?: string,
    exifDate?: string,
  ) {
    const config = await injectTokenToConfig(
      {
        signal,
      },
      {
        customer: customerId,
        site: siteId,
      },
    );
    const entityUrlPart = uploadTypeToPilotApiUrlPart(uploadType);
    const payload = flightScheduleId ? { flightScheduleId, ...(exifDate ? { exifDate } : {}) } : {};

    return this.pilotApiService.put(
      `customers/${customerId}/sites/${siteId}/${entityUrlPart}/${date}/mark-loaded`,
      payload,
      config,
    );
  }

  async cancelIntake(
    customerId: CustomerId,
    siteId: SiteId,
    uploadType: UploadType,
    date: ScanDate,
    flightScheduleId?: string,
  ) {
    const config = await injectTokenToConfig(
      {},
      {
        customer: customerId,
        site: siteId,
      },
    );
    const entityUrlPart = uploadTypeToPilotApiUrlPart(uploadType);
    const payload = flightScheduleId ? { flightScheduleId } : {};

    return this.pilotApiService.put(
      `customers/${customerId}/sites/${siteId}/${entityUrlPart}/${date}/cancel`,
      payload,
      config,
    );
  }

  async setGeoref(
    signal: AbortSignal,
    customerId: CustomerId,
    siteId: SiteId,
    date: ScanDate,
    georefUploadPath: string,
    flightScheduleId?: string,
  ) {
    const config = await injectTokenToConfig(
      { signal },
      {
        customer: customerId,
        site: siteId,
      },
    );
    const entityUrlPart = uploadTypeToPilotApiUrlPart(UploadType.Ppk);
    const payload = flightScheduleId ? { flightScheduleId } : {};

    return this.pilotApiService.put(
      `customers/${customerId}/sites/${siteId}/${entityUrlPart}/${date}/georef`,
      { georef: georefUploadPath, ...payload },
      config,
    );
  }

  async getAvailableFeatures(): Promise<string[]> {
    const config = await injectTokenToConfig({}, null);
    const res = await this.pilotApiService.get('available-features', config);

    return res.data.features;
  }

  async getConfig(): Promise<ServerConfig> {
    const config = await injectTokenToConfig({}, null);
    const res = await this.pilotApiService.get('config', config);

    return res.data.config;
  }

  async sendInvoice(invoiceParams: {
    startDate: string;
    endDate: string;
    scanCosts?: number;
    panoCosts?: number;
    lidarCosts?: number;
    timeMaterialsFlightCosts?: number;
    gcpCosts?: number;
    gcpMaterialsCosts?: number;
    equipmentLeasingExpenses?: number;
    additionalCosts?: Array<{ description: string; amount: number }>;
    associatedMissions?: AssociatedMissionPayload[];
    timeMaterialsEntries?: TimeMaterialsEntryPayload[];
    paymentLink?: string;
    bankDetails?: string;
    invoiceLink?: string;
    invoiceFileName?: string;
    invoiceFileSize?: number;
    invoiceFileType?: string;
  }) {
    const config = await injectTokenToConfig({}, null);

    return this.pilotApiService.put('invoice', invoiceParams, { ...config, ...INVOICE_RETRY_CONFIG });
  }

  async uploadInvoice(file: File): Promise<string> {
    const config = await injectTokenToConfig({ headers: { 'Content-Type': 'multipart/form-data' } }, null);
    const formData = new FormData();

    formData.append('file', file);
    const res = await this.pilotApiService.post('upload-invoice', formData, { ...config, ...INVOICE_RETRY_CONFIG });

    return res.data.filePath;
  }

  async getPilotCosts(): Promise<ScanCostType> {
    const config = await injectTokenToConfig({}, null);
    const res = await this.pilotApiService.get('pilot-costs', config);

    return res.data;
  }

  async getInvoices(): Promise<InvoicesList> {
    const config = await injectTokenToConfig({}, null);
    const res = await this.pilotApiService.get('invoices', config);

    return res.data;
  }

  async cancelInvoice(id: string): Promise<void> {
    const config = await injectTokenToConfig({}, null);
    await this.pilotApiService.post(`invoice/${id}/cancel`, {}, config);
  }

  async updateInvoice(
    invoiceId: string,
    params: {
      // Resubmit replaces the whole invoice, so the period must be sent too.
      startDate?: string;
      endDate?: string;
      timeMaterialsFlightCosts?: number;
      scanCosts?: number;
      panoCosts?: number;
      lidarCosts?: number;
      gcpCosts?: number;
      gcpMaterialsCosts?: number;
      equipmentLeasingExpenses?: number;
      additionalCosts?: Array<{ description: string; amount: number }>;
      associatedMissions?: AssociatedMissionPayload[];
      timeMaterialsEntries?: TimeMaterialsEntryPayload[];
      paymentLink?: string;
      bankDetails?: string;
      invoiceLink?: string;
      // invoiceDoc.path from GET /invoices — references the already-stored document ("keep it
      // unchanged"); sent when the pilot didn't replace the file.
      invoicePath?: string;
      invoiceFileName?: string;
      invoiceFileSize?: number;
      invoiceFileType?: string;
    },
  ): Promise<void> {
    const config = await injectTokenToConfig({}, null);
    await this.pilotApiService.put(`invoice/${invoiceId}`, params, { ...config, ...INVOICE_RETRY_CONFIG });
  }

  async getPilotDocuments(): Promise<DocsResponse> {
    const config = await injectTokenToConfig({}, null);

    const res = await this.pilotApiService.get('pilot-documents', config);

    return res.data;
  }

  async uploadPilotAttachment(
    file: File,
    fileContext: FileUploadContext,
    signal?: AbortSignal,
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void,
  ): Promise<string> {
    const config = await injectTokenToConfig(
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: { fileContext },
        signal,
        onUploadProgress,
      },
      null,
    );

    const formData = new FormData();

    formData.append('file', file);
    const res = await this.pilotApiService.post('file-upload', formData, config);

    return res.data.filePaths[0];
  }

  async createPilotDocument(documentParams: {
    documentType: string;
    startDate?: string;
    endDate?: string;
    fileUrls: string[];
    coaProjectId?: string;
  }): Promise<void> {
    const config = await injectTokenToConfig({}, null);

    const res = await this.pilotApiService.post('pilot-document', documentParams, config);

    return res.data;
  }

  async updatePilotDocument(documentParams: {
    documentId: string;
    startDate?: string;
    endDate?: string;
    attachments: Array<{ url: string } | { id: string }>;
  }): Promise<void> {
    const config = await injectTokenToConfig({}, null);

    const res = await this.pilotApiService.put('pilot-document', documentParams, config);

    return res.data;
  }

  async getPresignedUploadUrl(
    signal: AbortSignal,
    customerId: CustomerId,
    siteId: SiteId,
    uploadPath: string,
  ): Promise<{ presignedUrl: string }> {
    const config = await injectTokenToConfig(
      { signal },
      {
        customer: customerId,
        site: siteId,
      },
    );

    const res = await this.pilotApiService.post(
      `customers/${customerId}/sites/${siteId}/upload/presigned`,
      { uploadPath },
      config,
    );

    return res.data;
  }

  async initiateMultipartUpload(
    signal: AbortSignal,
    customerId: CustomerId,
    siteId: SiteId,
    uploadPath: string,
  ): Promise<{ uploadId: string }> {
    const config = await injectTokenToConfig(
      { signal },
      {
        customer: customerId,
        site: siteId,
      },
    );

    const res = await this.pilotApiService.post(
      `customers/${customerId}/sites/${siteId}/upload/multipart`,
      { uploadPath },
      config,
    );

    return res.data;
  }

  async getMultipartPartUrl(
    signal: AbortSignal,
    customerId: CustomerId,
    siteId: SiteId,
    uploadPath: string,
    uploadId: string,
    partNumber: number,
  ): Promise<{ presignedUrl: string }> {
    const config = await injectTokenToConfig(
      { signal },
      {
        customer: customerId,
        site: siteId,
      },
    );

    const res = await this.pilotApiService.post(
      `customers/${customerId}/sites/${siteId}/upload/multipart/part-url`,
      { uploadPath, uploadId, partNumber },
      config,
    );

    return res.data;
  }

  async completeMultipartUpload(
    signal: AbortSignal,
    customerId: CustomerId,
    siteId: SiteId,
    uploadPath: string,
    uploadId: string,
    parts: Array<{ partNumber: number; etag: string }>,
  ): Promise<void> {
    const config = await injectTokenToConfig(
      { signal },
      {
        customer: customerId,
        site: siteId,
      },
    );

    await this.pilotApiService.post(
      `customers/${customerId}/sites/${siteId}/upload/multipart/complete`,
      { uploadPath, uploadId, parts },
      config,
    );
  }

  async getTimezoneFromCoordinates(params: { lat: number; lng: number }) {
    const config = await injectTokenToConfig({}, null);

    const { data } = await this.pilotApiService.get<string[]>('timezone/from-coordinates', {
      ...config,
      params,
    });

    return data;
  }
}

function uploadTypeToPilotApiUrlPart(uploadType: UploadType) {
  switch (uploadType) {
    case UploadType.Pano:
      return 'panos';
    case UploadType.RegularAndRtk:
      return 'scans';
    case UploadType.Ppk:
      return 'ppkscans';
    case UploadType.Lidar:
      return 'lidar-scans';
  }
}

export default function getPilotApiService() {
  const pilotApiServiceBaseUrl = query_string_utils.checkIfExperimental()
    ? PilotApiExperimentalServiceBaseUrl
    : PilotApiServiceBaseUrl;

  return new PilotApiService(pilotApiServiceBaseUrl);
}

export function validationResultsToPilotApiMetrics(results: ValidationResult[]): PilotApiValidationResults {
  const metrics = {} as PilotApiValidationResults;

  for (const result of results) {
    const metricKey = result.name;
    const { metric, isValid } = result;

    metrics[metricKey] = [isValid, Number(metric)];
  }

  return metrics;
}

/**
 * Cleans up messy Airtable mission fields
 */
export const cleanUpMissionData = (fetchedMissions: MissionCustomer[]) => {
  for (const missionCustomer of fetchedMissions) {
    for (const site of missionCustomer.sites) {
      if (site.projectInfo) {
        const cleaned = site.projectInfo.panoramaLocations?.replace(/[*~_/\\$%]/g, '') ?? '';
        site.projectInfo.parsedPanoramaLocations = cleaned
          .split('\n')
          .map((line) => line.trim())
          .filter((line) => line.length > 0);
      }
    }
  }
};
